import $ from 'jquery';
import Swiper from 'swiper';
import _ from 'underscore';
import autosize from 'lib/autosize';
import { jqGetElemById, $body, $window, isMobile } from 'util/global';
import { getToken } from 'util/csrf';
import { track } from 'util/analytics';
import ajax from 'util/ajax';
import { EVT_DAILYPROBLEM_MAINBODY_EXPANDED } from 'util/constants';
import { setStateForSolvable, solvableVkeyHandler } from 'app/solvables';
import { modalController } from 'util/ui';

let challengeCalendarSwiper;

/** Strip the hash from the URL */
function removeLocationHash() {
    const fullPath = window.location.pathname + window.location.search;
    window.history.replaceState({}, document.title, fullPath);
}

/**
 * Handler for the snooze-reminders modal
 * @param {JQuery} $container
 */
function reminderSnoozeHandler($container) {
    const snoozeRemindersHash = '#snooze-reminders';
    const $form = $container.find('.dp-reminder-snooze-form');
    const $modal = $container.find('.dp-reminder-snooze-modal');

    const trackSnoozeDays = (snoozeDays) => {
        track('ui', {
            action: 'click',
            element_id: 'clicked_dailyproblems_snooze_reminders_save',
            snooze_days: snoozeDays,
        });
    };

    $form.on('submit', () => {
        $modal.modal('hide');
        const snoozeDays = parseInt($form.find('[name=snooze_days]:checked').val(), 10);
        trackSnoozeDays(snoozeDays);
    });

    // handle the disable-snooze button differently from the rest of the form
    $container.find('.snooze-disable-trigger').on('click', (e) => {
        e.preventDefault();

        const pageKey = $container.data('page-key');
        ajax(pageKey).form($form[0], { snooze_days: 0 });

        $modal.modal('hide');

        trackSnoozeDays(0);
    });

    if (window.location.hash === snoozeRemindersHash) {
        $modal.modal('show');
        removeLocationHash();
    }
}

/**
 * Handle showing reminder modals
 * @param {JQuery} $container
 */
export function reminders($container) {
    const setRemindersHash = '#set-reminders';
    const $reminderModal = $container.find('.dp-reminder-set');
    const showReminderModal = () => {
        $reminderModal.modal();
        track('ui', {
            action: 'modal',
            element_type: 'modal',
            element_id: 'viewed_dailyproblems_reminders_modal',
        });
        if (window.location.hash === setRemindersHash) {
            removeLocationHash();
        }
    };

    // show on click
    $container.find('.set-reminder-trigger').on('click', showReminderModal);

    // show if there are form errors
    if ($reminderModal.find('.errorlist').length) {
        showReminderModal();
    }

    // hide the modal when submitting the form
    $reminderModal.find('form').on('submit', () => {
        $reminderModal.modal('hide');

        const eventProps = {
            action: 'click',
            element_id: 'clicked_dailyproblems_reminders_save',
        };

        // Add track prefs to eventProps
        $reminderModal.find('[name=tracks]').each((index, el) => {
            eventProps[`track_${el.dataset.track}`] = el.checked;
        });

        track('ui', eventProps);
    });

    reminderSnoozeHandler($container);

    // automatically open the reminder modal if the hash matches
    if (window.location.hash === setRemindersHash) {
        showReminderModal();
    }
}


const checkForDailyProblemChange = (type, $form) => {
    // the modal might be updated on save
    let $outdatedDailyCallengeModal = jqGetElemById(`outdated-daily-challenge-${type}-modal`);
    const $saveOutdatedDailyChallengeModal = jqGetElemById(`save-outdated-daily-challenge-${type}-modal`);
    let checkDailyProblemChangeTimeout;

    _.defer(() => {
        const poll = () => {
            $outdatedDailyCallengeModal = jqGetElemById(`outdated-daily-challenge-${type}-modal`);
            const data = $outdatedDailyCallengeModal.data();
            $.get(data.diffUrl).then((response) => {
                // the daily problem has been updated
                if (response.latest_change_id &&
                    response.latest_change_id !== data.latestChange) {
                    // show outdated daily problem modal
                    $outdatedDailyCallengeModal.modal();
                    clearTimeout(checkDailyProblemChangeTimeout);
                }
            });
            checkDailyProblemChangeTimeout = setTimeout(poll, 10000);
        };
        poll();
    });

    $outdatedDailyCallengeModal.find('.discard-btn').on('click', () => {
        // reload the page
        window.location.reload();
    });

    return (event) => {
        if (checkDailyProblemChangeTimeout) {
            event.preventDefault();
            // stop polling for current daily problem change id
            clearTimeout(checkDailyProblemChangeTimeout);
            const data = $outdatedDailyCallengeModal.data();

            // get current daily problem id one more time
            $.get(data.diffUrl).then((response) => {
                // the daily problem id has been updated
                if (response.latest_change_id && response.latest_change_id !== data.latestChange) {
                    // show outdated daily problem id modal
                    $saveOutdatedDailyChallengeModal.modal();
                    clearTimeout(checkDailyProblemChangeTimeout);
                } else {
                    $form.submit();
                }
            });
            $saveOutdatedDailyChallengeModal.find('.save-btn').on('click', () => $form.submit());
        }
    };
};


/**
 * Expandable mainbody text, editable daily problem
 * @param {JQuery} $container
 */
export function mainbodySection($container) {
    // expandable mainbody section
    const $expandBtn = jqGetElemById('dp-expand');
    if ($expandBtn.length) {
        $expandBtn.one('click', () => {
            $container.addClass('expanded');
            $window.trigger($.Event(EVT_DAILYPROBLEM_MAINBODY_EXPANDED));
        });
    }
    const $form = $container.find('.dp-mainbody-form');
    // editable daily problem
    const editingClass = 'editing';
    let saveMainBody = () => { };
    const showEditForm = () => {
        $container.addClass(editingClass);
        $container.find('textarea').trigger('autosize.resize');
        saveMainBody = checkForDailyProblemChange('main', $form);
    };
    if (window.location.hash === '#edit_challenge') {
        showEditForm();
    }
    jqGetElemById('dp-edit').on('click', showEditForm);
    jqGetElemById('dp-edit-cancel').on('click', () => $container.removeClass(editingClass));
    jqGetElemById('dp-save-mainbody').on('click', (event) => {
        saveMainBody(event);
    });

    // series and position in series
    const $series = jqGetElemById('id_series');
    const $insertAfterId = jqGetElemById('id_insert_after_id');
    const seriesItems = $series.data('series-items');
    let insertAfterIdInitVal = $insertAfterId.data('initial-value');

    const updateInsertAfterIdField = () => {
        const seriesId = +$series.val();
        let optionsHTML = '';

        if (seriesId > 0) {
            optionsHTML += '<option value="0">[make it the first item]</option>';
            seriesItems[seriesId].forEach((item, i, arr) => {
                let selected = '';
                if (insertAfterIdInitVal !== undefined && item.id === insertAfterIdInitVal ||
                    insertAfterIdInitVal === undefined && i === arr.length - 1) {
                    selected = ' selected';
                }
                optionsHTML += `<option value="${item.id}"${selected}>${item.title}</option>`;
            });
        }

        $insertAfterId.html(optionsHTML).prop('disabled', seriesId === 0);
        insertAfterIdInitVal = undefined;
    };

    $series.on('change', updateInsertAfterIdField);
    updateInsertAfterIdField();

    // apply autosize to all textareas
    autosize($container.find('textarea'));
}


/**
 * Hide riskier fields to make it harder to accidentally change something
 * @param {JQuery} $container
 */
export function hiddenField($container) {
    $container.find('button').on('click', (e) => {
        e.preventDefault();
        $container.addClass('visible');
        $container.find('input').focus();
    });
}


/**
 * Tracks clicks on DC cards.
 * @param {JQuery} $card
 */
export function card($card) {
    $card.on('click', () => {
        const {
            imageType, isSeries, hasStaffNote, hasPreviewCopy, isComplete, isArchived,
        } = $card.data();
        const eventParams = {
            action: 'click',
            element_type: 'link',
            element_id: 'clicked_daily_problem_card',
            is_series: isSeries,
            has_staff_note: hasStaffNote,
            has_preview_copy: hasPreviewCopy,
            is_complete: isComplete,
            is_archived: isArchived,

        };
        if (imageType) {
            eventParams.image_type = imageType;
        }
        track('ui', eventParams);
    });
}


/**
 * Handle edit buttons in the problem selector component
 * @param {JQuery} $container
 */
export function problemAdminButtons($container) {
    $container.children('.edit-problem').on('click', () => {
        $('.question-text').trigger('edit');
        checkForDailyProblemChange('problem');
    });

    $container.children('.edit-answer').on('click', () => {
        $('.solvable-mcq-options-btn').trigger('click');
        checkForDailyProblemChange('problem');
    });
}

/**
 * Dict of categorizable inputs
 * @type {Object.<JQuery>}
 */
const $categorizableInputs = {};

/**
 * A category name, one of 'course', 'chapter', or 'quiz'
 * @typedef {string} Category
 */

/**
 * Get the categorization data for the category and value
 * @param {Category} category
 * @param {*} value - Current value for the category
 * @returns {Promise}
 */
function getCategorizationData(category, value) {
    const data = {
        category_type: category,
        selected_category_id: value,
    };

    const opts = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-CSRFToken': getToken(),
        },
        credentials: 'include',
        redirect: 'follow',
        body: JSON.stringify(data),
    };

    return fetch('/assessment/get_course_categorization_options/', opts)
        .then(response => response.json());
}

/**
 * Set the target category value and update other fields if necessary, e.g. selecting a course
 * filters the chapter and quiz dropdowns
 * @param {Category} category
 * @param {*} value
 */
function changeCategorizableInput(category, value) {
    if (!value) {
        return;
    }

    // Set a specific value
    const updateCategoryExact = (targetCategory, targetValue) => {
        $categorizableInputs[targetCategory].val(targetValue);
    };

    // Filter select options
    const updateCategoryList = (targetCategory, ids) => {
        // wrapping disabled options in hidden spans because safari won't hide them otherwise
        $categorizableInputs[targetCategory].find('span.hide option[disabled]').unwrap();
        $categorizableInputs[targetCategory].find('option')
            .prop('disabled', false)
            .filter((i, option) => {
                if (option.value === '') {
                    // always allow null option
                    return false;
                }
                const intValue = parseInt(option.value, 10);
                return ids.indexOf(intValue) < 0;
            })
            .prop('disabled', true)
            .wrapAll('<span class="hide"></span>');

        const currentValue = parseInt($categorizableInputs[targetCategory].val(), 10);
        const isCurrentValueDisabled = ids.indexOf(currentValue) < 0;
        if (isCurrentValueDisabled) {
            $categorizableInputs[targetCategory].val(null);
        }
    };

    getCategorizationData(category, value)
        .then((result) => {
            const { data } = result;

            // handle specific id
            ['course', 'chapter', 'quiz'].forEach((targetCategory) => {
                const targetValue = data[`${targetCategory}_id`];
                if (targetValue) {
                    updateCategoryExact(targetCategory, targetValue);
                }
            });

            // handle filtered id list
            if (data.chapters) {
                updateCategoryList('chapter', data.chapters);
            }
            if (data.quizzes) {
                updateCategoryList('quiz', data.quizzes);
            }
        });
}

/**
 * Handle the set of categorizable select inputs. These are the course/chapter/quiz admin selects
 * that each depend on each other, i.e. selecting an option in one of them can select or filter
 * the options in the other selects
 * @param {JQuery} $field
 */
export function categorizableInput($field) {
    const category = $field.data('field');
    const $select = $field.find('select');

    $categorizableInputs[category] = $select;

    $select.on('change', () => changeCategorizableInput(category, $select.val()));

    if (Object.keys($categorizableInputs).length === 3) {
        const courseVal = $categorizableInputs.course.val();
        if (courseVal) {
            changeCategorizableInput('course', courseVal);
        }
    }
}

/**
 * Sidebar should scroll with the rest of the page when the content is too long to fit the screen.
 * @param {JQuery} $sidebar
 */
export function checkSidebarHeight($sidebar) {
    const STATIC_SIDEBAR_CLASS = 'static-sidebar';
    const EXTRA_MARGIN = 75; // room for the dropdown + some margin
    const $lastChild = $sidebar.children().last();
    const sidebarBottom = $lastChild.offset().top + $lastChild.outerHeight() + EXTRA_MARGIN;
    const checkHeight = () => {
        $body.toggleClass(STATIC_SIDEBAR_CLASS, window.innerHeight < sidebarBottom);
    };

    $window.on('resize.dpSidebar', _.throttle(checkHeight, 16));
    checkHeight();
}

/**
 * Expandable calendar section for mobile
 * @param {JQuery} $container
 */
export function expandableCalendar($container) {
    $container.find('h3').on('click', () => {
        $container.toggleClass('expanded');
        challengeCalendarSwiper.update();
    });
}

/**
 * Intercept any clicks and send the user to the NUX
 * @param {JQuery} $container
 */
export function nuxRedirect($container) {
    $container.find('a, button').on('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = $container.data('next');
    });
}

export function nuxModal($modal) {
    const promises = [import('dailyproblems_nux.less')];
    if (isMobile) {
        promises.push(import('mobile/dailyproblems_nux.less'));
    }
    Promise.all(promises).then(() => modalController($modal));
}

export function addToClassroomModal($modal) {
    track('ui', {
        action: 'click',
        element_type: 'button',
        element_id: 'clicked_schools_add_daily_challenge_to_class',
    });
    modalController($modal);
}

window.vkey.daily_problems_sidebar = (action, actionKey) => {
    $('#dailyproblems-addToClassroom:visible')
        .modal('hide')
        .remove();
    window.vkey.handle(action, actionKey);
};

export function challengeCalendar($calendar) {
    // only one of these is supposed to be in the calendar
    const $markedDay = $calendar.find('.today, .current').first();

    if (isMobile) {
        const $slides = $calendar.find('.days');
        const $currentSlide = $slides.filter((i, elem) => $.contains(elem, $markedDay[0])).first();

        challengeCalendarSwiper = new Swiper($calendar.find('.swiper-container')[0], {
            initialSlide: $slides.index($currentSlide),
            pagination: {
                el: $calendar.find('.swiper-pagination')[0],
            },
        });
    } else {
        // desktop

        if (!$markedDay.length) {
            return;
        }
        const cellHeight = parseInt($calendar.find('.days').css('grid-template-rows'), 10);
        // scroll so that the current day is always on the second row
        $calendar.scrollTop($markedDay.position().top - cellHeight - 15);
    }
}

/** @param {JQuery} $trigger */
export function explanationsTrigger($trigger) {
    $trigger.on('click', (e) => {
        e.preventDefault();
        setStateForSolvable($trigger.parents('.solv-main'), 'solutions');
    });
}

window.vkey.logged_in_daily_problems_problem = (action, actionKey) => {
    const $component = solvableVkeyHandler(action, actionKey);
    const isAttempted = $component.parents('.solv-main').find('.endstate').length > 0;
    const $challengeCalendar = $('.challenge-calendar');

    if ($challengeCalendar.length && isAttempted) {
        $challengeCalendar.find('.day.current').addClass('completed');
    }
};
